import {
  Alert,
  AlertModal,
  Button,
  Card,
  Col,
  Form,
  FutureArticleHeader,
  List,
  ListCell,
  ListRow,
  Placeholder,
  Row,
  Submit,
} from "@administrate/piston-ux";
import {
  FormActions,
  useTypedFormValues,
} from "@administrate/piston-ux/lib/Form";
import { RawStaticInput } from "@administrate/piston-ux/lib/StaticInput";
import { ensureObservableValue } from "@administrate/piston-ux/lib/withFormWrapper";
import { get } from "lodash";
import { observer } from "mobx-react-lite";
import { Fragment, FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import NavigationPrompt from "react-router-navigation-prompt";
import { getContactVariables } from "../../components/ContactPicker";
import {
  LearnerType,
  LearnersFormValues,
  MultipleLearnerSelector,
  MultipleLearnerSelectorProps,
} from "../../components/LearnerSelection/MultipleLearnerSelector";
import { UNNAMED_LEARNER_CONTACT } from "../../components/LearnerSelection/selectedContact";
import {
  Event as EventType,
  LearningPath,
  PointOfSaleFieldEntityType,
  PointOfSaleFieldFilter,
  Query,
} from "../../generated/weblinkTypes";
import {
  useWebLinkClient,
  useWebLinkMutation,
  useWebLinkQuery,
} from "../../hooks/weblink";
import {
  ADD_CART_LINE_ITEM_MUTATION,
  POINT_OF_SALE_LEARNER_FIELD_QUERY,
} from "../../queries/cart";
import { GET_MANAGED_CONTACTS_FOR_EVENT } from "../../queries/learnerManagement";
import { useHistory } from "../../useHistory";
import { mapPointOfSaleFieldValues } from "../../utils/customFieldValues";
import { extractNodes } from "../../utils/extractNodes";

type LearnerSelectionProps = {
  product: EventType | LearningPath | undefined;
  cartId: string | null | undefined;
  loading: boolean;
  pointOfSaleFieldVariables: {
    entityType: PointOfSaleFieldEntityType;
    filters?: PointOfSaleFieldFilter[];
  };
  learningPathObjectivesInput?: { eventId: string; objectiveId: string }[];
  maxLearners: number | null | undefined;
  getMaxLearnersReachedMessages?: MultipleLearnerSelectorProps["getMaxLearnersReachedMessages"];
  invalidStateAlertVisible: boolean;
};

export const LearnerSelection = observer<LearnerSelectionProps>(
  ({
    cartId,
    product,
    pointOfSaleFieldVariables,
    learningPathObjectivesInput,
    maxLearners,
    getMaxLearnersReachedMessages,
    invalidStateAlertVisible,
  }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [apiError, setApiError] = useState(false);
    const [
      addCartLineItem,
      { loading: mutationLoading, error: mutationError },
    ] = useWebLinkMutation(ADD_CART_LINE_ITEM_MUTATION);

    const { data: pointOfSaleFieldsData, loading: pointOfSaleFieldsLoading } =
      useWebLinkQuery<Pick<Query, "pointOfSaleLearnerFields">>(
        POINT_OF_SALE_LEARNER_FIELD_QUERY,
        {
          variables: pointOfSaleFieldVariables,
        },
      );
    const pointOfSaleFields =
      pointOfSaleFieldsData && pointOfSaleFieldsData.pointOfSaleLearnerFields
        ? pointOfSaleFieldsData.pointOfSaleLearnerFields
        : [];

    const values = useTypedFormValues<LearnersFormValues>({
      learnerNumber: 1,
      learners: [
        {
          id: "unnamed",
          learnerNumber: 0,
          contact: UNNAMED_LEARNER_CONTACT,
          posFieldValues: {},
        },
      ],
      learnerLength: 1,
    });

    if (pointOfSaleFields.length > 0) {
      values.learners.forEach((learner: LearnerType) => {
        pointOfSaleFields.forEach((field) => {
          ensureObservableValue(learner.posFieldValues, field.key);
        });
      });
    }

    const client = useWebLinkClient();
    const baseVariables =
      product && product.__typename === "Event" ? { eventId: product.id } : {};
    const contacts = (inputValue: string) =>
      client
        .query({
          query: GET_MANAGED_CONTACTS_FOR_EVENT,
          variables: { ...baseVariables, ...getContactVariables(inputValue) },
        })
        .then((result) => {
          return [
            UNNAMED_LEARNER_CONTACT,
            ...extractNodes(result.data.managedContacts.edges),
          ];
        });

    const handleSubmit = async () => {
      const response = await addCartLineItem({
        variables: {
          input: {
            cartId: cartId,
            cartLineItem: getCartLineItem(),
          },
        },
      });
      if (response.errors && response.errors.length > 0) {
        console.error(
          "error while adding opportunity line item",
          response.errors,
        );
        return;
      }

      const responseCartId = get(
        response,
        "data.cart.addLineItem.cart.id",
        null,
      );
      const apiErrors = get(response, "data.cart.addLineItem.errors", null);

      if ((apiErrors && apiErrors.length > 0) || !responseCartId) {
        console.error(
          "Encountered an error while creating Cart",
          cartId,
          apiErrors,
        );
        setApiError(true);
        return;
      }

      history.push(`/catalog/coordinating/booking/${responseCartId}`);
    };

    const getCartLineItem = () => {
      const existingLearnerDetailsInput = values.learners
        .filter((learner: LearnerType) => learner.id !== "unnamed")
        .map((learner: LearnerType) => {
          const posFieldValues = mapPointOfSaleFieldValues(
            pointOfSaleFields,
            learner.posFieldValues,
          );
          return {
            contactId: learner.id,
            attributes: posFieldValues,
          };
        });

      return {
        productOptionId: product && product.id,
        quantity: values.learners.length,
        learners: {
          newLearners: [],
          existingLearners: existingLearnerDetailsInput,
        },
        objectives: learningPathObjectivesInput
          ? learningPathObjectivesInput
          : [],
      };
    };

    return (
      <Fragment>
        {pointOfSaleFieldsLoading ? (
          <LearnerSelectionSkeleton />
        ) : (
          <Form
            type="raw"
            values={values}
            onSubmit={handleSubmit}
            disabled={mutationLoading}
          >
            <Card>
              <FutureArticleHeader title={t("learners")} />
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <NavigationPrompt
                when={(_, nextLocation) => {
                  return (
                    !invalidStateAlertVisible &&
                    values.learners.length > 0 &&
                    !get(nextLocation, "pathname", "").includes("booking")
                  );
                }}
              >
                {({ onConfirm, onCancel }) => (
                  <AlertModal
                    title={`${t(
                      "Are you sure you want to leave this page? Your current booking will be lost",
                    )}.`}
                    show
                    onDone={(confirmed: boolean) => {
                      return confirmed ? onConfirm() : onCancel();
                    }}
                    type="warning"
                  />
                )}
              </NavigationPrompt>
              {(mutationError || apiError) && (
                <Alert
                  type="error"
                  message={`${t(
                    "An error occurred while adding learners to your cart",
                  )}.`}
                  glyph="removeSign"
                  overPage={false}
                />
              )}
              <MultipleLearnerSelector
                contacts={contacts}
                values={values}
                maxLearners={maxLearners}
                pointOfSaleFields={pointOfSaleFields}
                getMaxLearnersReachedMessages={getMaxLearnersReachedMessages}
              />
              <FormActions>
                <Submit label={t("continue")} />
              </FormActions>
            </Card>
          </Form>
        )}
      </Fragment>
    );
  },
);

export const LearnerSelectionSkeleton: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  return (
    <Card>
      <FutureArticleHeader title={t("learners")} />
      <Row className="mb-2">
        <Col xs={12}>
          <RawStaticInput value="" label={t("numberOfLearners")} />
          <Placeholder width={200} />
        </Col>
      </Row>
      <hr />
      <List extraClass="learners">
        <ListRow gridColumns="140px 1fr">
          <ListCell label={t("learnerNumber")}>
            <Placeholder />
          </ListCell>
          <ListCell
            label={t("learnerInformation")}
            extraClass="learners--information"
          >
            <Placeholder />
          </ListCell>
        </ListRow>
      </List>
      <div className="my-3"></div>
      <FormActions>
        <Button type="primary" disabled label={t("continue")} />
      </FormActions>
    </Card>
  );
};
